import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquarePlus,
  Lightbulb,
  HelpCircle,
  Bug,
  Sparkles,
  MoreHorizontal,
  CheckCircle2,
  Loader2,
  ArrowRight,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";

/**
 * Feedback & Ideas — "Help shape Postmoro".
 *
 * Deliberately simple: one form, one success state. No history list,
 * no comments/voting (out of scope). Reuses existing premium
 * primitives (PageHeader, GlassCard, GradientMesh) so it feels
 * native to the rest of the Postmoro app.
 *
 * Backend contract: POST /api/feedback with
 *   { category, title, message, importance? }
 * Everything else (user_id, email, workspace, plan) is attached
 * server-side — the user never has to re-enter their own metadata.
 */

const CATEGORIES = [
  { key: "feature_request",  label: "Feature request",     icon: Sparkles,      hint: "A capability you wish existed" },
  { key: "improvement_idea", label: "Improvement idea",    icon: Lightbulb,     hint: "Make something existing better" },
  { key: "confusing",        label: "Something confusing", icon: HelpCircle,    hint: "Copy or flow that's unclear" },
  { key: "bug",              label: "Bug / issue",         icon: Bug,           hint: "Something isn't working right" },
  { key: "other",            label: "Other",               icon: MoreHorizontal, hint: "Anything else on your mind" },
];

const IMPORTANCE = [
  { key: "",               label: "Not sure" },
  { key: "nice_to_have",   label: "Nice to have" },
  { key: "important",      label: "Important" },
  { key: "very_important", label: "Very important" },
];

const TITLE_MAX = 100;
const MESSAGE_MAX = 2000;

function CategoryCard({ cat, selected, onSelect }) {
  const Icon = cat.icon;
  const isActive = selected === cat.key;
  return (
    <button
      type="button"
      data-testid={`feedback-category-${cat.key}`}
      onClick={() => onSelect(cat.key)}
      className={
        "text-left rounded-xl border transition-all duration-200 px-4 py-3 h-full " +
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-primary)] " +
        (isActive
          ? "bg-[color:rgba(27,168,159,0.10)] border-[color:var(--brand-primary)] shadow-[0_0_0_1px_rgba(27,168,159,0.35)]"
          : "bg-[color:var(--surface-2)] border-[color:var(--border-subtle)] hover:border-[color:rgba(27,168,159,0.45)]")
      }
    >
      <div className="flex items-center gap-2.5">
        <span
          className={
            "inline-flex h-8 w-8 items-center justify-center rounded-lg " +
            (isActive
              ? "bg-[color:rgba(27,168,159,0.18)] text-[color:var(--brand-primary)]"
              : "bg-[color:var(--surface-3)] text-[color:var(--text-2)]")
          }
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="text-sm font-medium text-[color:var(--text-1)]">{cat.label}</span>
      </div>
      <div className="mt-1.5 text-xs text-[color:var(--text-3)] leading-snug">{cat.hint}</div>
    </button>
  );
}

function GoodFeedbackPanel() {
  const bullets = [
    "A feature you wish existed",
    "Something that takes too many steps",
    "Copy or flow that's confusing",
    "An idea that could save you time",
  ];
  return (
    <GlassCard className="p-5" data-testid="feedback-good-panel">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-primary)]">
        <Lightbulb className="h-3.5 w-3.5" />
        Good feedback can be
      </div>
      <ul className="mt-3 space-y-2">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-sm text-[color:var(--text-2)] leading-relaxed">
            <span className="mt-1.5 inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--brand-primary)]" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-4 border-t border-[color:var(--border-subtle)] text-xs text-[color:var(--text-3)] leading-relaxed">
        We read every submission. Not every request becomes a feature, but the pattern of what you share directly shapes what we build next.
      </div>
    </GlassCard>
  );
}

function SuccessState({ onAnother }) {
  const navigate = useNavigate();
  return (
    <GlassCard className="p-8 text-center" data-testid="feedback-success">
      <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-[color:rgba(27,168,159,0.14)] text-[color:var(--brand-primary)]">
        <CheckCircle2 className="h-7 w-7" />
      </div>
      <h2 className="mt-4 text-2xl font-semibold text-[color:var(--text-1)]">
        Thanks for helping shape Postmoro.
      </h2>
      <p className="mt-2 text-sm text-[color:var(--text-2)] max-w-lg mx-auto leading-relaxed">
        We review every suggestion and use feedback like yours to guide future improvements.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          onClick={onAnother}
          data-testid="feedback-submit-another-btn"
          variant="outline"
          className="min-w-[180px]"
        >
          <MessageSquarePlus className="h-4 w-4 mr-2" />
          Submit another idea
        </Button>
        <Button
          onClick={() => navigate("/dashboard")}
          data-testid="feedback-back-dashboard-btn"
          className="min-w-[180px] bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-black"
        >
          Return to Dashboard
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </GlassCard>
  );
}

export default function Feedback() {
  const [category, setCategory] = useState("feature_request");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [importance, setImportance] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  const resetForm = useCallback(() => {
    setCategory("feature_request");
    setTitle("");
    setMessage("");
    setImportance("");
    setSucceeded(false);
  }, []);

  const canSubmit =
    !!category &&
    title.trim().length > 0 &&
    title.length <= TITLE_MAX &&
    message.trim().length > 0 &&
    message.length <= MESSAGE_MAX &&
    !submitting;

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!canSubmit) return;
      setSubmitting(true);
      try {
        await api.post("/feedback", {
          category,
          title: title.trim(),
          message: message.trim(),
          ...(importance ? { importance } : {}),
        });
        setSucceeded(true);
        toast.success("Feedback sent — thank you!");
      } catch (err) {
        const detail =
          err?.response?.data?.detail || err?.message || "Something went wrong sending your feedback.";
        // 429 rate-limit shows the friendly retry-after text as-is
        toast.error(String(detail));
      } finally {
        setSubmitting(false);
      }
    },
    [canSubmit, category, title, message, importance]
  );

  return (
    <div className="relative min-h-screen">
      <GradientMesh />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="feedback-page">
        <PageHeader
          eyebrow="Help shape Postmoro"
          title="We Value Your Opinion"
          description="Tell us what you'd like to see improved, added, or changed. Your feedback helps us decide what to build next."
          icon={MessageSquarePlus}
          testidPrefix="feedback-hero"
        />

        {succeeded ? (
          <div className="mt-8 max-w-2xl mx-auto">
            <SuccessState onAnother={resetForm} />
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
            <form onSubmit={onSubmit} data-testid="feedback-form">
              <GlassCard className="p-5 sm:p-6">
                {/* CATEGORY */}
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--text-3)]">
                    Category <span className="text-[color:var(--brand-primary)]">*</span>
                  </label>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {CATEGORIES.map((cat) => (
                      <CategoryCard
                        key={cat.key}
                        cat={cat}
                        selected={category}
                        onSelect={setCategory}
                      />
                    ))}
                  </div>
                </div>

                {/* TITLE */}
                <div className="mt-6">
                  <div className="flex items-baseline justify-between">
                    <label htmlFor="fb-title" className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--text-3)]">
                      Title <span className="text-[color:var(--brand-primary)]">*</span>
                    </label>
                    <span
                      className={
                        "text-[11px] " +
                        (title.length > TITLE_MAX
                          ? "text-[color:var(--danger,#ef4444)]"
                          : "text-[color:var(--text-3)]")
                      }
                    >
                      {title.length}/{TITLE_MAX}
                    </span>
                  </div>
                  <input
                    id="fb-title"
                    data-testid="feedback-title-input"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX + 20))}
                    maxLength={TITLE_MAX + 20}
                    placeholder="One-line summary of your idea"
                    className="mt-2 w-full rounded-lg bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] px-3 py-2.5 text-sm text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary)] focus:border-transparent transition"
                  />
                </div>

                {/* MESSAGE */}
                <div className="mt-5">
                  <div className="flex items-baseline justify-between">
                    <label htmlFor="fb-message" className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--text-3)]">
                      Message <span className="text-[color:var(--brand-primary)]">*</span>
                    </label>
                    <span
                      className={
                        "text-[11px] " +
                        (message.length > MESSAGE_MAX
                          ? "text-[color:var(--danger,#ef4444)]"
                          : "text-[color:var(--text-3)]")
                      }
                    >
                      {message.length}/{MESSAGE_MAX}
                    </span>
                  </div>
                  <textarea
                    id="fb-message"
                    data-testid="feedback-message-input"
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX + 40))}
                    rows={7}
                    placeholder="Describe your idea, the problem it solves, or what would make it better. The more context you share, the more useful it is."
                    className="mt-2 w-full rounded-lg bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] px-3 py-2.5 text-sm text-[color:var(--text-1)] placeholder:text-[color:var(--text-3)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary)] focus:border-transparent transition resize-y min-h-[140px]"
                  />
                </div>

                {/* IMPORTANCE */}
                <div className="mt-5">
                  <label htmlFor="fb-importance" className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--text-3)]">
                    Importance <span className="text-[color:var(--text-3)] normal-case tracking-normal">(optional)</span>
                  </label>
                  <select
                    id="fb-importance"
                    data-testid="feedback-importance-select"
                    value={importance}
                    onChange={(e) => setImportance(e.target.value)}
                    className="mt-2 w-full rounded-lg bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] px-3 py-2.5 text-sm text-[color:var(--text-1)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand-primary)] focus:border-transparent transition"
                  >
                    {IMPORTANCE.map((opt) => (
                      <option key={opt.key || "none"} value={opt.key}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* SUBMIT */}
                <div className="mt-7 flex justify-end">
                  <Button
                    type="submit"
                    disabled={!canSubmit}
                    data-testid="feedback-submit-btn"
                    className="min-w-[180px] bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-black disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <MessageSquarePlus className="h-4 w-4 mr-2" />
                        Send Feedback
                      </>
                    )}
                  </Button>
                </div>
              </GlassCard>
            </form>

            {/* Right-side panel (desktop only) */}
            <div className="hidden lg:block">
              <GoodFeedbackPanel />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
